<template>
  <div
    :class="{ 'tags-slider': !isMobile }"
    data-qa="reviews_tags_slider"
  >
    <div :class="{ 'tags-slider__track': !isMobile }">
      <div
        class="pt-2"
        :class="{
          'd-flex': isMobile,
          'tags-slider__wrapper swiper-wrapper': !isMobile,
        }"
      >
        <v-sheet
          v-for="tag in tagsList"
          :key="tag.translit"
          class="pa-4 mr-2 tags-slider__slide"
          data-qa="reviews_tags_slide"
          :elevation="tag.isActive ? 0 : 2"
          :color="tag.isActive ? 'bg-primary' : 'bg-gray-0'"
          rounded
          @click="handleClick(tag)"
        >
          <div
            class="text-subtitle-2 text--text mb-2 text-no-wrap"
            data-qa="reviews_tags_name"
          >
            {{ tag.name }}
          </div>
          <div class="d-flex align-center justify-start">
            <div class="d-flex align-center mr-4">
              <v-icon
                :color="tag.isActive ? 'text-info' : 'secondary'"
              >
                ui-icon-heart-active
              </v-icon>
              <div
                class="text-body-2 text--text ml-1"
                data-qa="reviews_tags_count_liked"
              >
                {{ tag.countLikedRates }}
              </div>
            </div>
            <div class="d-flex align-center">
              <v-icon
                :color="tag.isActive ? 'text-info' : 'button-text-snackbars'"
              >
                ui-icon-heart-broken
              </v-icon>
              <div
                class="text-body-2 text--text ml-1"
                data-qa="reviews_tags_count_disliked"
              >
                {{ tag.countDislikedRates }}
              </div>
            </div>
          </div>
        </v-sheet>
      </div>
    </div>
    <div
      v-if="!isMobile"
      class="tags-slider__nav"
    >
      <div
        class="tags-slider__nav-button tags-slider__nav-prev"
        data-qa="reviews_tags_prev_button"
      >
        <span class="ui-icon-fz-medium ui-icon-arrow-left" />
      </div>
      <div
        class="tags-slider__nav-button tags-slider__nav-next"
        data-qa="reviews_tags_next_button"
      >
        <span class="ui-icon-fz-medium ui-icon-arrow-right" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Swiper } from 'swiper'
import type { Tag } from 'www/vue_components/common/ReviewsTags/types'

type Emits = {
  (e: 'change:tag', tag: Tag): void // eslint-disable-line
}

const emit = defineEmits<Emits>()
const props = withDefaults(defineProps<{
  tagsList: Array<Tag>
}>(), {
  tagsList: () => [],
})
const tagsList = ref(props.tagsList)
const isMobile = window.MOBILE_VERSION

function handleClick(tag: Tag) {
  tagsList.value.forEach((i: Tag) => {
    if (i !== tag) {
      i.isActive = false
    }
  })

  tag.isActive = !tag.isActive
  emit('change:tag', tag)
}

onMounted(() => {
  if (!isMobile) {
    new Swiper('.tags-slider__track', {
      loop: false,
      wrapperClass: 'tags-slider__wrapper',
      slideClass: 'tags-slider__slide',
      slidesPerView: 'auto',
      navigation: {
        nextEl: '.tags-slider__nav-next',
        prevEl: '.tags-slider__nav-prev',
        disabledClass: 'tags-slider__nav-disabled',
      },
    })
  }
})
</script>

<style scoped lang="scss">
@import '~www/themes/doctors/common/variables';

.tags-slider {
  position: relative;
  width: calc(100% + 32px);
  margin-left: -8px;

  &__track {
    padding: 8px;
    overflow: hidden;
  }

  &__slide {
    cursor: pointer;
  }

  &__nav {
    width: calc(100% + 40px);
    height: 40px;
    display: flex;
    position: absolute;
    top: calc(50% - 20px);
    left: -20px;
    justify-content: space-between;
  }

  &__nav-button {
    opacity: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 100%;
    color: $ui-kit-primary;
    cursor: pointer;
    border-radius: $border-radius-round;
    background-color: $ui-kit-bg-gray-0;
    transition: 0.2s ease-in-out;

    @include new-elevation-2;
  }

  &__nav-disabled {
    cursor: default;
    color: $ui-kit-icon-secondary;
  }

  &:hover &__nav-button {
    opacity: 1;
  }
}
</style>
